import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Typography,
    Paper,
    TableContainer,
    TablePagination,
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import autoRefreshedExecuteGetFetchOrgUserDataWithToken, { OrgUsersData } from '../tools/getFetchOrgUserData';
import autoRefreshedExecuteHasAllClientOrgLicenseStatusWithToken from '../tools/permissions/hasAllClientOrgLicenseStatus';
import DomainAddIcon from '@mui/icons-material/DomainAdd'
import Checkbox from '@mui/material/Checkbox';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { alpha } from '@mui/material/styles';
import WidgetsIcon from '@mui/icons-material/Widgets';
import SchoolIcon from '@mui/icons-material/School';
import autoRefreshedExecuteGetCreateAppUserWithToken from '../tools/getCreateAppUser';
import autoRefreshedExecuteGetUpdateAppUserWithToken from '../tools/getUpdateAppUser';
import autoRefreshedExecuteGetCreateClassWithToken from '../tools/getCreateClass';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import autoRefreshedExecuteGetUpdateClassWithToken from '../tools/getUpdateClass';

interface ModalConfig {
    title: string;
    fields: { label: string; name: string; type?: string }[];
    onSubmit: (data: Record<string, string>) => void;
}

const ModalForm: React.FC<{
    open: boolean;
    onClose: () => void;
    config: ModalConfig;
    data: Record<string, string>;
    errors: Record<string, boolean>;
    responseErrorMessage: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}> = ({ open, onClose, config, data, errors, responseErrorMessage, onChange }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>{config.title}</DialogTitle>
        <DialogContent>
            {config.fields.map((field) => (
                field.type === 'select' ? (
                    <TextField
                        key={field.name}
                        select
                        label={field.label}
                        name={field.name}
                        value={data[field.name]}
                        onChange={onChange}
                        error={errors[field.name]}
                        helperText={errors[field.name] ? `${field.label} is required` : ''}
                        fullWidth
                        margin="normal"
                        SelectProps={{
                            native: true,
                        }}
                    >
                        <option value=""></option>
                        <option value="Student">Student</option>
                        <option value="Teacher">Teacher</option>
                        <option value="Admin">Admin</option>
                        <option value="Owner">Owner</option>
                    </TextField>
                ) : (
                    <TextField
                        key={field.name}
                        label={field.label}
                        name={field.name}
                        type={field.type || 'text'}
                        value={data[field.name]}
                        onChange={onChange}
                        error={errors[field.name]}
                        helperText={errors[field.name] ? `${field.label} is required` : ''}
                        fullWidth
                        margin="normal"
                    />
                )
            ))}
            {Object.values(errors).some((error) => error) && (
                <Typography color="error" variant="body2" style={{ marginTop: '16px' }}>
                    Please correct the highlighted fields before proceeding.
                </Typography>
            )}
            {responseErrorMessage && (
                <Typography color="error" variant="body2" style={{ marginTop: '16px' }}>
                    {responseErrorMessage}
                </Typography>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color="secondary">
                Cancel
            </Button>
            <Button
                onClick={() => {
                    if (!Object.values(errors).some((error) => error)) {
                        config.onSubmit(data);
                    }
                }}
                color="primary"
            >
                Apply
            </Button>
        </DialogActions>
    </Dialog>
);

const UserAdmin: React.FC = () => {
    const navigate = useNavigate()
    const accessToken = localStorage.getItem('access_token');
    const [hasAllPermission, setHasAllPermission] = React.useState<boolean | null>(null);
    const [usersData, setUsersData] = useState<OrgUsersData | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
    const [modalData, setModalData] = useState<Record<string, string>>({});
    const [modalErrors, setModalErrors] = useState<Record<string, boolean>>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [responseErrorMessage, setResponseErrorMessage] = useState("");


    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value.toLowerCase());
    };

    const openModal = (config: ModalConfig) => {
        setModalConfig(config);
        setModalData(config.fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}));
        setModalErrors(config.fields.reduce((acc, field) => ({ ...acc, [field.name]: false }), {}));
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalConfig(null);
        setModalData({});
        setModalErrors({});
        setResponseErrorMessage("");
    };

    const handleModalInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setModalData({ ...modalData, [name]: value });
    };

    const handleAddUser = () => {
        openModal({
            title: 'Create New User',
            fields: [
                { label: 'Student Name', name: 'studentName' },
                { label: 'Email', name: 'email' },
                { label: 'Class', name: 'classRoomName' },
                { label: 'Password', name: 'password', type: 'password' },
                { label: 'Role', name: 'role', type: 'select' },
            ],
            onSubmit: async (data) => {
                const errors = {
                    studentName: data.studentName.trim() === '',
                    email: data.email.trim() === '',
                    classRoomName: data.classRoomName.trim() === '',
                    password: data.password.trim() === '',
                    role: data.role.trim() === '',
                };
                setModalErrors(errors);

                if (!Object.values(errors).some((error) => error)) {
                    try {
                        const response = await autoRefreshedExecuteGetCreateAppUserWithToken(accessToken, {
                            studentName: data.studentName,
                            email: data.email,
                            classRoomName: data.classRoomName,
                            password: data.password,
                            role: data.role,
                        });
                        console.log('User created successfully:', response);
                        closeModal();
                        const fetchedUsers = await autoRefreshedExecuteGetFetchOrgUserDataWithToken(accessToken);
                        setUsersData(fetchedUsers);
                        setResponseErrorMessage('');
                    } catch (error) {
                        try {
                            setResponseErrorMessage(`${(error as any).response.data.error}`);
                        } catch (error) {
                            setResponseErrorMessage('An unknown error occurred while creating the user.');
                        }
                        console.error('Error creating user:', error);
                    }
                }
            },
        });
    };

    const handleAddClass = () => {
        openModal({
            title: 'Create New Class',
            fields: [
                { label: 'New Class Name', name: 'newClassName' },
            ],
            onSubmit: async (data) => {
                const errors = {
                    newClassName: data.newClassName.trim() === '',
                };
                setModalErrors(errors);

                if (!Object.values(errors).some((error) => error)) {
                    try {
                        const response = await autoRefreshedExecuteGetCreateClassWithToken(accessToken, {
                            newClassName: data.newClassName,
                        });
                        console.log('New class created successfully:', response);
                        closeModal();
                        const fetchedUsers = await autoRefreshedExecuteGetFetchOrgUserDataWithToken(accessToken);
                        setUsersData(fetchedUsers);
                        setResponseErrorMessage('');
                    } catch (error) {
                        try {
                            setResponseErrorMessage(`${(error as any).response.data.error}`);
                        } catch (error) {
                            setResponseErrorMessage('An unknown error occurred while creating the user.');
                        }
                        console.error('Error creating user:', error);
                    }
                }
            },
        });
    };

    const handleUpdateAppUser = () => {
        if (selected.length !== 1 || !usersData) {
            setResponseErrorMessage('Please select exactly one user to update.');
            return;
        }

        const selectedUser = usersData.userData.find((user) => user.email === selected[0]);
        if (!selectedUser) {
            setResponseErrorMessage('Selected user not found.');
            return;
        }

        openModal({
            title: 'Change App User Info',
            fields: [
                { label: 'Current Email', name: 'currentEmail' },
                { label: 'New Email', name: 'newEmail' },
                { label: 'Student Name', name: 'studentName' },
                { label: 'Class', name: 'classRoomName' },
                { label: 'New Password', name: 'password', type: 'password' },
                { label: 'Role', name: 'role', type: 'select' },
            ],
            onSubmit: async (data) => {
                const errors = {
                    currentEmail: data.currentEmail.trim() === '',
                };
                setModalErrors(errors);

                if (!Object.values(errors).some((error) => error)) {
                    try {
                        const response = await autoRefreshedExecuteGetUpdateAppUserWithToken(accessToken, {
                            currentEmail: data.currentEmail,
                            newEmail: data.newEmail,
                            studentName: data.studentName,
                            classRoomName: data.classRoomName,
                            password: data.password,
                            role: data.role,
                        });
                        console.log('User updated successfully:', response);
                        closeModal();
                        const fetchedUsers = await autoRefreshedExecuteGetFetchOrgUserDataWithToken(accessToken);
                        setUsersData(fetchedUsers);
                        setResponseErrorMessage('');
                    } catch (error) {
                        try {
                            setResponseErrorMessage(`${(error as any).response.data.error}`);
                        } catch (error) {
                            setResponseErrorMessage('An unknown error occurred while updating the user.');
                        }
                        console.error('Error updating user:', error);
                    }
                }
            },
        });

        setModalData({
            currentEmail: selectedUser.email,
            newEmail: selectedUser.email,
            studentName: selectedUser.studentName,
            classRoomName: selectedUser.classRoomName || '',
            password: '',
            role: selectedUser.userRole,
        });
    };

    const handleUpdateClass = () => {
        if (selected.length !== 1 || !usersData) {
            setResponseErrorMessage('Please select exactly one user to update.');
            return;
        }

        const selectedUser = usersData.userData.find((user) => user.email === selected[0]);
        if (!selectedUser) {
            setResponseErrorMessage('Selected user not found.');
            return;
        }

        openModal({
            title: 'Class Name Bulk Update',
            fields: [
                { label: 'Target Class Name', name: 'currentClass' },
                { label: 'New Class Name', name: 'newClass' },
            ],
            onSubmit: async (data) => {
                const errors = {
                    currentClass: data.currentClass.trim() === '',
                };
                setModalErrors(errors);

                if (!Object.values(errors).some((error) => error)) {
                    try {
                        const response = await autoRefreshedExecuteGetUpdateClassWithToken(accessToken, {
                            currentClass: data.currentClass,
                            newClass: data.newClass,
                        });
                        console.log('Class updated successfully:', response);
                        closeModal();
                        const fetchedUsers = await autoRefreshedExecuteGetFetchOrgUserDataWithToken(accessToken);
                        setUsersData(fetchedUsers);
                        setResponseErrorMessage('');
                    } catch (error) {
                        try {
                            setResponseErrorMessage(`${(error as any).response.data.error}`);
                        } catch (error) {
                            setResponseErrorMessage('An unknown error occurred while updating the class.');
                        }
                        console.error('Error updating class:', error);
                    }
                }
            },
        });

        setModalData({
            currentClass: selectedUser.classRoomName || '',
        });
    };

    const handleActivateStatus = () => {
        if (selected.length !== 1 || !usersData) {
            setResponseErrorMessage('Please select exactly one user to update.');
            return;
        }

        const selectedUser = usersData.userData.find((user) => user.email === selected[0]);
        if (!selectedUser) {
            setResponseErrorMessage('Selected user not found.');
            return;
        }

        openModal({
            title: 'Manage User Status (Activate/Deactivate)',
            fields: [
                { label: 'Email', name: 'Email' },
                { label: 'Type Activate or Deactivate', name: 'newStatus', type: 'text' },
            ],
            onSubmit: async (data) => {
                const errors = {
                    Email: data.Email.trim() === '',
                    newStatus: data.newStatus.trim() === '' || !['Activate', 'Deactivate'].includes(data.newStatus.trim()),
                };
                setModalErrors(errors);

                if (!Object.values(errors).some((error) => error)) {
                    const _role = data.newStatus === "Activate" ? "Student" : data.newStatus === "Deactivate" ? "In-Active" : null;

                    try {
                        const response = await autoRefreshedExecuteGetUpdateAppUserWithToken(accessToken, {
                            currentEmail: data.Email,
                            newEmail: null,
                            studentName: null,
                            classRoomName: null,
                            password: null,
                            role: _role,
                        });
                        console.log('User updated successfully:', response);
                        closeModal();
                        const fetchedUsers = await autoRefreshedExecuteGetFetchOrgUserDataWithToken(accessToken);
                        setUsersData(fetchedUsers);
                        setResponseErrorMessage('');
                    } catch (error) {
                        try {
                            setResponseErrorMessage(`${(error as any).response.data.error}`);
                        } catch (error) {
                            setResponseErrorMessage('An unknown error occurred while updating the user.');
                        }
                        console.error('Error updating user:', error);
                    }
                }
            },
        });

        setModalData({
            Email: selectedUser.email,
        });
    };

    const formatDate = (date: string | null): string => {
        if (!date) return 'N/A';
        const parsedDate = new Date(date);
        return `${parsedDate.toISOString().split('T')[0]} ${parsedDate.toISOString().split('T')[1].slice(0, 5)}`;
    };

    const sortUsersByLastLogin = (users: OrgUsersData | null): OrgUsersData | null => {
        if (!users) return null;
        const sortedUserData = [...users.userData].sort((a, b) => {
            const dateA = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
            const dateB = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
            return dateB - dateA; // Descending order
        });
        return { ...users, userData: sortedUserData };
    };

    const sortedAndFilteredUsers = sortUsersByLastLogin(usersData)
        ? sortUsersByLastLogin(usersData)?.userData.filter((user) =>
            user.studentName.toLowerCase().includes(searchQuery)
        )
        : [];

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked && usersData) {
            const newSelected = usersData.userData.map((user) => user.email);
            setSelected(newSelected);
        } else {
            setSelected([]);
        }
    };

    const handleClick = (event: React.MouseEvent<unknown>, email: string) => {
        if (selected.includes(email)) {
            setSelected([]); // Uncheck if the row is already selected
        } else {
            setSelected([email]); // Select the row
        }
    };

    const isSelected = (email: string) => selected.indexOf(email) !== -1;

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await autoRefreshedExecuteHasAllClientOrgLicenseStatusWithToken(accessToken, "project", "dummyKey", ["professional, add"]);
                setHasAllPermission(response.has_all_rights); // Update permission state
            } catch (error) {
                console.error("Error fetching data:", error);
                setHasAllPermission(false); // Default to no permission on error
            }
        };

        fetchData();
    }, [accessToken]);


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const accessToken = localStorage.getItem('accessToken');
                const fetchedUsers = await autoRefreshedExecuteGetFetchOrgUserDataWithToken(accessToken);
                setUsersData(fetchedUsers);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchUsers();
    }, []);

    const EnhancedTableToolbar: React.FC<{ numSelected: number }> = ({ numSelected }) => (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                </Typography>
            )}
            {numSelected > 0 && (
                <>

                    <Tooltip title="Change Class Name">
                        <IconButton>
                            <SchoolIcon onClick={handleUpdateClass} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="(De)Activate">
                        <IconButton>
                            <VerifiedUserIcon onClick={handleActivateStatus} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Update User Info">
                        <IconButton onClick={handleUpdateAppUser}>
                            <WidgetsIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )}
        </Toolbar>
    );

    if (hasAllPermission === null) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </div>
        );
    }


    return (
        <div style={{ padding: '0 25px' }}>
            <Typography
                gutterBottom
                align="center"
                style={{
                    padding: '24px 0',
                    fontSize: '24px',
                    fontWeight: 'lighter',
                    textDecoration: 'underline',
                    textDecorationColor: '#bbdefb',
                    textUnderlineOffset: '15px',
                }}
            >
                Organization Admin
            </Typography>
            <EnhancedTableToolbar numSelected={selected.length} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Button
                    variant="text"
                    style={{ backgroundColor: 'white', color: '#1976d2', borderColor: '#1976d2' }}
                    startIcon={<PersonAddIcon style={{ color: '#1976d2' }} />}
                    onClick={handleAddUser}
                >
                    Add User
                </Button>
                <Button
                    variant="text"
                    style={{ backgroundColor: 'white', color: '#1976d2', borderColor: '#1976d2' }}
                    startIcon={<DomainAddIcon style={{ color: '#1976d2' }} />}
                    onClick={handleAddClass}
                >
                    Add Class
                </Button>
                <TextField
                    label="Search by Student"
                    variant="standard"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ marginLeft: 'auto' }}
                />
            </div>
            <TableContainer component={Paper} style={{ maxHeight: 600 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    color="primary"
                                    indeterminate={false} // Disable indeterminate state
                                    checked={false} // Disable "select all" functionality
                                    inputProps={{
                                        'aria-label': 'select all users',
                                    }}
                                    disabled
                                />
                            </TableCell>
                            <TableCell>Student Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Last Login</TableCell>
                            <TableCell>Joined Since</TableCell>
                            <TableCell>Last Exam</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Org. License Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedAndFilteredUsers
                            ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((user, index) => {
                                const isItemSelected = isSelected(user.email);
                                const labelId = `enhanced-table-checkbox-${index}`;

                                return (
                                    <TableRow
                                        hover
                                        onClick={(event) => handleClick(event, user.email)}
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={user.email}
                                        selected={isItemSelected}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                                inputProps={{
                                                    'aria-labelledby': labelId,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell component="th" id={labelId} scope="row">
                                            {user.studentName}
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.classRoomName}</TableCell>
                                        <TableCell>{formatDate(user.lastLogin)}</TableCell>
                                        <TableCell>{formatDate(user.dateJoined)}</TableCell>
                                        <TableCell>{formatDate(user.lastExamDate)}</TableCell>
                                        <TableCell>{user.userRole}</TableCell>
                                        <TableCell>{usersData?.orgLicenseStatus}</TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[20, 25, 50]}
                component="div"
                count={sortedAndFilteredUsers?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            {modalConfig && (
                <ModalForm
                    open={isModalOpen}
                    onClose={closeModal}
                    config={modalConfig}
                    data={modalData}
                    errors={modalErrors}
                    responseErrorMessage={responseErrorMessage}
                    onChange={handleModalInputChange}
                />
            )}
        </div>
    );
};

export default UserAdmin;
